import { makeStyles, Tooltip } from '@material-ui/core';
import clsx from 'clsx';
import {
    areIntervalsOverlapping,
    differenceInCalendarDays,
    eachDayOfInterval,
    eachWeekOfInterval,
    endOfDay,
    endOfWeek,
    isSameMonth,
    isToday,
    startOfWeek,
} from 'date-fns';
import React from 'react';
import { usePrefixTranslation } from '../../../i18n';
import { theme } from '../../../theme/theme';
import { Button } from '../forms/Button';
import { CalendarContext, CalendarEvent } from './Calendar';
import { Event } from './Event';
import { CalendarTrack, createTracks, denseArrangeTracks } from './util';
import { useMonthClasses as useYearMonthClasses } from './YearView';

const useMonthViewClasses = makeStyles({
    root: {
        display: 'grid',
        gridAutoRows: 'max-content',
        gridTemplateColumns: 'repeat(7, 1fr)',
        alignItems: 'start',
        overflow: 'auto',
        flex: '1 0 0',
        '&.drag-over > div:not(.dragging)': { pointerEvents: 'none' },
    },
    weekday: {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        padding: 8,
        borderRight: `1px solid ${theme.colors.neutral[6]}`,
        borderBottom: `1px solid ${theme.colors.neutral[6]}`,
        color: theme.colors.neutral[2],
        backgroundColor: 'white',
        mixBlendMode: 'normal',
        ...theme.fonts.h6,
        textTransform: 'uppercase',
    },
    cell: {
        alignSelf: 'stretch',
        minHeight: 120,
        borderRight: `1px solid ${theme.colors.neutral[6]}`,
        borderBottom: `1px solid ${theme.colors.neutral[6]}`,
        '&.month': { backgroundColor: 'white' },
        '&:not(.month)': { background: theme.colors.neutral[4] },
    },
    day: {
        position: 'sticky',
        top: 30,
        zIndex: 1,
        padding: 8,
        borderRight: `1px solid ${theme.colors.neutral[6]}`,
        '&.month': { backgroundColor: 'white' },
        '&:not(.month)': { backgroundColor: theme.colors.neutral[4] },
        mixBlendMode: 'normal',
        transition: `all ${theme.transitions.default}s`,
        '&.drag-over': { boxShadow: `0 0 4px 2px ${theme.colors.neutral[3]} inset` },
        '& > button': {
            padding: 4,
            borderRadius: 2,
            color: theme.colors.neutral[2],
            backgroundColor: 'transparent',
            ...theme.fonts.h6,
        },
        '& > button:hover': { backgroundColor: theme.colors.neutral[4] },
        '&.now > button': { color: 'white', backgroundColor: theme.colors.blue[1] },
        '&.now > button:hover': { backgroundColor: theme.colors.blue[1] },
    },
    event: {
        overflow: 'hidden',
        margin: '2px 8px',
        '&:not(.month)': { opacity: 0.4 },
        '&.first': { marginTop: 4 },
        '&.last': { marginBottom: 8 },
    },
});

/**
 * Calendar month view.
 *
 * @param props.context calendar context
 */
export const MonthView = (props: { context: React.Context<CalendarContext> }) => {
    const classes = useMonthViewClasses();
    const yearMonthClasses = useYearMonthClasses();
    const { t } = usePrefixTranslation('global', { global: 'calendar.MonthView' });
    const { events, intervals, setView, setAnchor, onFilter, onDnD, onExternalDnD } = React.useContext(props.context);
    const weeksInterval = { start: startOfWeek(intervals.month.start), end: endOfWeek(intervals.month.end) };
    const weeksIntervals = eachWeekOfInterval(weeksInterval).map(start => ({ start, end: endOfWeek(start) }));
    const monthEvents = React.useMemo(
        () => events.filter(event => areIntervalsOverlapping(event.interval, weeksInterval)),
        [events, +weeksInterval.start, +weeksInterval.end],
    );
    const initialGridRow = 2;
    const weeksTracks = React.useMemo(
        () =>
            weeksIntervals
                .map(week => eachDayOfInterval(week).map(start => ({ start, end: endOfDay(start) })))
                .map(resolution => denseArrangeTracks(createTracks(monthEvents, resolution)))
                .reduce<{ tracks: CalendarTrack[]; levels: number; start: number; end: number }[]>((acc, tracks) => {
                    const previous = acc[acc.length - 1];
                    const start = !previous ? initialGridRow : previous.end + 1;
                    const end = start + tracks.levels;
                    acc.push({ ...tracks, start, end });
                    return acc;
                }, []),
        [monthEvents],
    );
    const [dragEvent, setDragEvent] = React.useState<CalendarEvent>();
    const [dragDate, setDragDate] = React.useState<Date>();

    React.useLayoutEffect(() => onFilter?.(monthEvents), [onFilter, monthEvents]);

    const cells = weeksIntervals.flatMap((interval, i) => {
        const { start, end } = weeksTracks[i];
        return eachDayOfInterval(interval).map(day => {
            const buttonGridArea = `${start}/${1 + (day.getDay() % 7)}/auto/auto`;
            const cellGridArea = `${start}/${1 + (day.getDay() % 7)}/${end + 1}/auto`;
            const cls = {
                now: isToday(day),
                month: isSameMonth(day, intervals.month.start),
                'drag-over': +day === +(dragDate ?? 0),
            };
            const dragEvents: JSX.IntrinsicElements['div'] = {
                onDragEnter: () => (onExternalDnD || dragEvent) && setDragDate(day),
                onDragOver: e => (onExternalDnD || dragEvent) && e.preventDefault(),
                onDragLeave: e => +day === +(dragDate ?? 0) && setDragDate(undefined),
                onDrop: e => {
                    setDragEvent(undefined);
                    if (!dragEvent) return onExternalDnD?.(e, day);
                    const difference = differenceInCalendarDays(day, dragEvent.interval.start);
                    if (difference !== 0) onDnD?.(dragEvent, { days: difference });
                },
            };
            return (
                <React.Fragment key={`cell-${+day}`}>
                    <span className={clsx(classes.cell, cls)} style={{ gridArea: cellGridArea }} {...dragEvents} />
                    <Tooltip classes={{ tooltip: yearMonthClasses.tooltip }} title={t('view-week')}>
                        <span className={clsx(classes.day, cls)} style={{ gridArea: buttonGridArea }} {...dragEvents}>
                            <Button onClick={() => (setView('week'), setAnchor(day))}>{day.getDate()}</Button>
                        </span>
                    </Tooltip>
                </React.Fragment>
            );
        });
    });

    const eventNodes = weeksTracks.flatMap(({ tracks, start, levels }, i) =>
        tracks.map((track, j) => {
            const gridArea = `${start + track.level + 1}/${1 + track.ticks.start}/auto/${2 + track.ticks.end}`;
            const cls = {
                dragging: track.events[0] === dragEvent,
                month: areIntervalsOverlapping(track.interval, intervals.month, { inclusive: true }),
                first: track.level === 0,
                last: track.level === levels - 1,
            };
            return (
                <Event
                    key={`event-${i * 1000000 + j}`}
                    className={clsx(classes.event, cls)}
                    style={{ gridArea }}
                    event={track.events[0]}
                    view='month'
                    draggable={track.events[0].draggable && !!onDnD}
                    onDragStart={() => setDragEvent(track.events[0])}
                    onDragEnd={() => setDragEvent(undefined)}
                />
            );
        }),
    );

    return (
        <div
            className={clsx(classes.root, 'scroll-shadow')}
            style={{ ['--shadow-offset-t' as any]: '67px' }}
            onDragStart={e => e.currentTarget.classList.add('drag-over')}
            onDrop={e => e.currentTarget.classList.remove('drag-over')}
            onDragEnd={e => e.currentTarget.classList.remove('drag-over')}
        >
            {eachDayOfInterval({ start: startOfWeek(new Date()), end: endOfWeek(new Date()) }).map(day => (
                <span key={+day} className={classes.weekday} style={{ gridArea: `1/${1 + day.getDay()}/auto/auto` }}>
                    {t('day', { date: day })}
                </span>
            ))}
            {cells}
            {eventNodes}
        </div>
    );
};
